import React from "react";
import { useQueryBuilder } from "./useQueryBuilder";
import LocationScopeSelector from "./LocationScopeSelector";
import DatasetSelector from "./DatasetSelector";
import FieldSelector from "./FieldSelector";
import FilterBuilder from "./FilterBuilder";
import SummaryBuilder from "./SummaryBuilder";
import PreviewPanel from "./PreviewPanel";
import SavedQueriesPanel from "./SavedQueriesPanel";
import "./QueryBuilder.css";

/**
 * Query Builder page. Composes the data/selection hook with presentational
 * sub-components: pick a dataset, choose fields, add filters, and preview the
 * resulting rows. The builder is metadata-driven — it renders itself from the
 * module metadata the backend returns, with no per-module logic here.
 * @param {object} props
 * @param {() => void} [props.onOpenReminders] switches to the reminder rules
 *   section; the shortcut is hidden when the host provides no way to navigate.
 * @returns {JSX.Element}
 */
export default function QueryBuilder({ onOpenReminders }) {
  const qb = useQueryBuilder();

  return (
    <div className="qb-root">
      <header className="qb-header">
        <p className="qb-muted">
          Build a dataset by choosing filters and rules, then preview the rows.
        </p>
        {onOpenReminders && (
          <button
            type="button"
            className="qb-link"
            onClick={onOpenReminders}
          >
            Manage reminders →
          </button>
        )}
      </header>

      <section className="qb-panel">
        <LocationScopeSelector
          options={qb.locationOptions}
          value={qb.locationIds}
          onChange={qb.setLocationIds}
        />

        <DatasetSelector
          module={qb.module}
          datasets={qb.datasets}
          onChange={qb.selectModule}
        />

        {qb.metaLoading && <p className="qb-muted">Loading dataset…</p>}
        {qb.metaError && (
          <div className="qb-error" role="alert">
            {qb.metaError}
          </div>
        )}

        {qb.metadata && (
          <>
            <FieldSelector
              fields={qb.visibleFields}
              selected={qb.fields}
              onToggle={qb.toggleField}
            />
            <FilterBuilder
              filters={qb.filters}
              fields={qb.visibleFields}
              onAdd={qb.addFilter}
              onChange={qb.updateFilter}
              onRemove={qb.removeFilter}
            />
            <SummaryBuilder
              fields={qb.visibleFields}
              aggregations={qb.aggregations}
              groupBy={qb.groupBy}
              onAddAggregation={qb.addAggregation}
              onChangeAggregation={qb.updateAggregation}
              onRemoveAggregation={qb.removeAggregation}
              onToggleGroupBy={qb.toggleGroupBy}
            />
          </>
        )}
      </section>

      {qb.metadata && (
        <section className="qb-panel">
          <PreviewPanel
            preview={qb.preview}
            onRun={qb.runPreview}
            disabled={qb.fields.length === 0}
            fields={qb.visibleFields}
          />
        </section>
      )}

      <section className="qb-panel">
        <SavedQueriesPanel
          getConfig={qb.getConfig}
          onLoad={qb.loadConfig}
          canSave={!!qb.metadata && qb.fields.length > 0}
          knownDatasets={qb.datasets.map((d) => d.key)}
        />
      </section>
    </div>
  );
}
